<template>
	<div class="desktop-transactions-page" :class="colourMode">
		<SettingsOverlay
			v-if="showSettings"
			:customer="customer"
			:colourMode="colourMode"
			:safeTop="safeTop"
			@close="showSettings = false"
			@update-pref="updatePref"
			@select-colour-mode="selectColourMode"
		/>
		<div v-else class="transactions-content">
			<!-- Page Header -->
			<!-- <div class="page-header">
				<h1 class="page-title">Transactions</h1>
			</div> -->

			<!-- Loading State -->
			<div v-if="!bankInfoLoaded" class="loading-state">
				<v-skeleton-loader type="card" height="120px" class="mb-4" />
				<v-skeleton-loader type="button" width="300px" class="mb-4" />
				<v-skeleton-loader type="table-row" v-for="i in 5" :key="i" />
			</div>

			<template v-else>
				<!-- Account Summary Card -->
				<div class="account-summary-card" :style="cardAccentStyle">
					<div class="card-header">
						<div class="bank-logo-frame" :style="cardGradientStyle">
							<img
								v-if="selectedAccount?.bank?.logoUri"
								:src="selectedAccount.bank.logoUri"
								class="bank-logo"
								alt="Bank logo"
							/>
							<div class="bank-logo-placeholder" v-else>
								{{ selectedAccount ? (selectedAccount.bank?.name || 'A').charAt(0) : '£' }}
							</div>
						</div>
						<div class="bank-details">
							<div class="bank-name">
								{{ selectedAccount ? (selectedAccount.bank?.prettyName || selectedAccount.bank?.name || 'Account') : 'All Accounts' }}
							</div>
							<div class="account-number">
								{{ selectedAccount ? (selectedAccount.accountType?.prettyName || 'Account') : 'Combined View' }}
								<span v-if="selectedAccount?.accountNumber"> · ••••{{ selectedAccount.accountNumber.slice(-4) }}</span>
							</div>
						</div>
					</div>
					<div class="card-balances">
						<div class="stat-column stat-column-small">
							<span class="stat-label">Balance</span>
							<span class="stat-amount" v-html="formatBalanceWithPence(selectedAccount ? selectedAccount.balance : totalBalance)"></span>
						</div>
						<div class="stat-column stat-column-small">
							<span class="stat-label">Transactions</span>
							<span class="stat-amount">{{ transactionStats.count }}</span>
						</div>
						<div class="stat-column stat-column-small">
							<span class="stat-label">Spent</span>
							<span class="stat-amount" v-html="formatBalanceWithPence(transactionStats.totalSpent)"></span>
						</div>
						<div class="stat-column stat-column-merchant" v-if="transactionStats.topMerchant">
							<span class="stat-label">Top Merchant</span>
							<span class="stat-amount stat-merchant">{{ transactionStats.topMerchant }}</span>
							<span class="stat-subtext">{{ formatter.format(transactionStats.topMerchantAmount) }}</span>
						</div>
					</div>
				</div>

				<!-- Uncategorized Banner -->
				<div v-if="uncategorizedTransactions.length > 0" class="uncategorized-banner">
					<div class="banner-content">
						<v-icon size="20">mdi-tag-outline</v-icon>
						<span class="banner-text">
							You have <strong>{{ uncategorizedTransactions.length }}</strong> uncategorized transaction{{ uncategorizedTransactions.length === 1 ? '' : 's' }}
						</span>
					</div>
					<button class="start-categorizing-btn" @click="startCategorizing">
						Start Categorising
					</button>
				</div>

				<!-- Filter Tabs -->
				<div class="filter-tabs-wrapper">
					<button
						v-show="canScrollLeft"
						class="scroll-arrow scroll-arrow-left"
						@click="scrollTabs('left')"
					>
						<v-icon size="20">mdi-chevron-left</v-icon>
					</button>
					<div class="filter-tabs" ref="filterTabs" @scroll="updateScrollArrows">
						<button
							v-for="tab in accountTabs"
							:key="tab.value"
							:class="['filter-tab', { active: selectedAccountFilter === tab.value }]"
							@click="selectedAccountFilter = tab.value"
						>
							<img
								v-if="tab.logoUri"
								:src="tab.logoUri"
								class="tab-logo"
								alt=""
							/>
							<span class="tab-content">
								<span class="tab-label">{{ tab.label }}</span>
								<span class="tab-balance">{{ formatter.format(tab.balance) }}</span>
							</span>
						</button>
					</div>
					<button
						v-show="canScrollRight"
						class="scroll-arrow scroll-arrow-right"
						@click="scrollTabs('right')"
					>
						<v-icon size="20">mdi-chevron-right</v-icon>
					</button>
				</div>

				<!-- Search & Filters -->
				<div class="search-section">
					<v-text-field
						v-model="searchQuery"
						prepend-inner-icon="mdi-magnify"
						density="compact"
						label="Search transactions"
						variant="outlined"
						hide-details
						clearable
						single-line
					/>
					<v-menu
						v-model="categoryMenuOpen"
						:close-on-content-click="false"
						location="bottom start"
					>
						<template v-slot:activator="{ props }">
							<button v-bind="props" class="category-filter-btn">
								<span>{{ selectedCategoryLabel }}</span>
								<v-icon size="18">mdi-chevron-down</v-icon>
							</button>
						</template>
						<v-card class="category-menu" :class="colourMode">
							<!-- All Categories -->
							<div
								class="category-menu-item"
								:class="{ selected: selectedCategoryFilter === 'all' }"
								@click="selectCategory('all', 'All Categories')"
							>
								All Categories
							</div>
							<!-- Uncategorized -->
							<div
								class="category-menu-item"
								:class="{ selected: selectedCategoryFilter === 'uncategorized' }"
								@click="selectCategory('uncategorized', 'Uncategorized')"
							>
								Uncategorized
							</div>
							<v-divider class="my-1" />
							<!-- Parent Categories -->
							<div v-for="parent in categoryFilterOptions" :key="parent.value" class="category-parent-group">
								<div
									class="category-parent-item"
									:class="{ selected: selectedCategoryFilter === parent.value }"
								>
									<span
										class="parent-selectable"
										@click="selectCategory(parent.value, parent.title)"
									>
										<span class="parent-color-dot" :style="{ backgroundColor: parent.color }"></span>
										<span class="parent-name">{{ parent.title }}</span>
									</span>
									<v-icon
										size="18"
										class="expand-icon"
										@click.stop="toggleParentExpand(parent.value)"
									>
										{{ expandedParents[parent.value] ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
									</v-icon>
								</div>
								<div v-if="expandedParents[parent.value]" class="category-children">
									<div
										v-for="child in parent.children"
										:key="child.value"
										class="category-child-item"
										:class="{ selected: selectedCategoryFilter === child.value }"
										@click="selectCategory(child.value, child.title)"
									>
										{{ child.title }}
									</div>
								</div>
							</div>
						</v-card>
					</v-menu>
					<!-- Tag Filter -->
					<v-menu
						v-model="tagMenuOpen"
						:close-on-content-click="false"
						location="bottom start"
					>
						<template v-slot:activator="{ props }">
							<button v-bind="props" class="tag-filter-btn">
								<v-icon size="18">mdi-tag-outline</v-icon>
								<span>{{ selectedTags.length ? `${selectedTags.length} tag${selectedTags.length > 1 ? 's' : ''}` : 'Tags' }}</span>
								<v-icon size="18">mdi-chevron-down</v-icon>
							</button>
						</template>
						<v-card class="tag-menu" :class="colourMode">
							<div v-if="availableTags.length === 0" class="no-tags">
								No tags available
							</div>
							<template v-else>
								<div
									v-for="tag in availableTags"
									:key="tag"
									class="tag-menu-item"
									@click="toggleTag(tag)"
								>
									<v-checkbox
										:model-value="selectedTags.includes(tag)"
										hide-details
										density="compact"
										class="tag-checkbox"
										@click.stop="toggleTag(tag)"
									/>
									<span class="tag-label">{{ tag }}</span>
								</div>
								<v-divider v-if="selectedTags.length" class="my-1" />
								<div
									v-if="selectedTags.length"
									class="clear-tags"
									@click="selectedTags = []"
								>
									Clear all
								</div>
							</template>
						</v-card>
					</v-menu>
					<!-- Date Range Filter -->
					<v-menu
						v-model="dateMenuOpen"
						:close-on-content-click="false"
						location="bottom start"
					>
						<template v-slot:activator="{ props }">
							<button v-bind="props" class="date-filter-btn">
								<v-icon size="18">mdi-calendar-range</v-icon>
								<span>{{ dateRangeLabel }}</span>
								<v-icon size="18">mdi-chevron-down</v-icon>
							</button>
						</template>
						<v-card class="date-menu" :class="colourMode">
							<div class="date-menu-content">
								<div class="date-field">
									<label>From</label>
									<input
										type="date"
										v-model="dateFrom"
										class="date-input"
									/>
								</div>
								<div class="date-field">
									<label>To</label>
									<input
										type="date"
										v-model="dateTo"
										class="date-input"
									/>
								</div>
							</div>
							<v-divider class="my-1" />
							<div class="date-menu-actions">
								<button class="clear-dates-btn" @click="clearDates">Clear</button>
								<button class="apply-dates-btn" @click="dateMenuOpen = false">Apply</button>
							</div>
						</v-card>
					</v-menu>
					<button class="refresh-btn" @click="refreshData">
						<v-icon size="18">mdi-refresh</v-icon>
					</button>
					<button class="download-csv-btn" @click="downloadCsv">
						<v-icon size="18">mdi-download</v-icon>
						<span>Export CSV</span>
					</button>
				</div>

				<!-- Transactions Table -->
				<TransactionsTable
					:transactions="displayTransactions"
					:colourMode="colourMode"
					:formatter="formatter"
					:highlightedTransactionId="selectedTransaction?.id"
					:availableTags="availableTags"
					:stickyHeaderTop="142"
					@tag-click="openTaggingModal"
					@tags-change="handleTagsChange"
				/>

				<!-- Search Further Back -->
				<div v-if="searchQuery && searchQuery.length >= 3" class="search-further-back">
					<button class="search-further-btn" @click="searchBackTransactions">
						<v-icon size="18">mdi-history</v-icon>
						<span>Search Further Back</span>
					</button>
				</div>
			</template>
		</div>

		<!-- Transaction Tagging Modal -->
		<TransactionTaggingModal
			:visible="showTransactionModal"
			:transaction-data="selectedTransaction"
			:colourMode="colourMode"
			:bulkMode="bulkMode"
			:bulkModeIndex="bulkModeIndex"
			:bulkModeTotal="bulkMode ? uncategorizedTransactions.length : 0"
			@close="closeModal"
			@tagged="onTransactionTagged"
			@next="onNextUncategorized"
		/>
	</div>
</template>

<script>
import SettingsOverlay from '../components/SettingsOverlay.vue';
import TransactionsTable from '../components/TransactionsTable.vue';
import TransactionTaggingModal from '../components/TransactionTaggingModal.vue';
import Services from '../../mixins/Services';
import RecurringPaymentsSidebar from '../../mixins/RecurringPaymentsSidebar';
import { GRANDPARENT_CLASSIFICATIONS } from '../../mixins/SpendingAnalysis';

export default {
	name: "TransactionsPage",
	mixins: [Services, RecurringPaymentsSidebar],
	components: { SettingsOverlay, TransactionsTable, TransactionTaggingModal },
	emits: ['colour-mode-changed'],
	watch: {
		searchQuery(newVal) {
			if (!newVal && this.selectedRecurringPaymentId) {
				this.clearRecurringPaymentFilter();
			}
		}
	},
	data() {
		return {
			colourMode: 'light',
			userName: 'there',
			showSettings: false,
			// Account data
			currentAccounts: [],
			creditAccounts: [],
			savingAccounts: [],
			// Filter & search state
			selectedAccountFilter: 'all',
			searchQuery: '',
			selectedCategoryFilter: 'all',
			selectedCategoryLabel: 'All Categories',
			categoryFilterOptions: [],
			categoryMenuOpen: false,
			expandedParents: {},
			// Tag filter
			selectedTags: [],
			tagMenuOpen: false,
			// Date filter
			dateMenuOpen: false,
			dateFrom: null,
			dateTo: null,
			// Modal state
			showTransactionModal: false,
			selectedTransaction: null,
			bulkMode: false,
			bulkModeIndex: 0,
			// Tags
			availableTags: [],
			// Loading
			bankInfoLoaded: false,
			// Scroll arrows
			canScrollLeft: false,
			canScrollRight: false
		}
	},
	computed: {
		safeTop() {
			return parseInt(this.$cookies.get('st') ?? 0);
		},
		allAccounts() {
			return [...this.currentAccounts, ...this.creditAccounts, ...this.savingAccounts];
		},
		accountTabs() {
			const tabs = [{ value: 'all', label: 'All Accounts', logoUri: null, balance: this.totalBalance }];
			this.allAccounts.forEach(account => {
				const bankName = account.bank?.name || account.provider || 'Account';
				const accountType = account.accountType?.prettyName || 'Account';
				tabs.push({
					value: account.id,
					label: `${bankName} - ${accountType}`,
					logoUri: account.bank?.logoUri,
					balance: account.balance
				});
			});
			return tabs;
		},
		cardGradientStyle() {
			const account = this.selectedAccount;
			if (account?.bank?.hexColour1 && account?.bank?.hexColour2) {
				const color1 = '#' + account.bank.hexColour1.slice(2);
				const color2 = '#' + account.bank.hexColour2.slice(2);
				return {
					background: `linear-gradient(135deg, ${color1} 0%, ${color2} 100%)`
				};
			}
			return { background: 'linear-gradient(135deg, #413655 0%, #5a4a70 100%)' };
		},
		cardAccentStyle() {
			const account = this.selectedAccount;
			if (account?.bank?.hexColour1) {
				const color = '#' + account.bank.hexColour1.slice(2);
				return { borderLeftColor: color };
			}
			return {};
		},
		selectedAccount() {
			if (this.selectedAccountFilter === 'all') return null;
			return this.allAccounts.find(a => a.id === this.selectedAccountFilter);
		},
		totalBalance() {
			return this.allAccounts.reduce((sum, a) => sum + (a.balance || 0), 0);
		},
		filteredTransactions() {
			if (!this.transactions) return [];
			if (this.selectedAccountFilter === 'all') return this.transactions;

			return this.transactions.map(group => {
				return group.filter(t => t.bankAccountID === this.selectedAccountFilter);
			}).filter(group => group.length > 0);
		},
		displayTransactions() {
			let result = this.filteredTransactions;

			// Category filter
			if (this.selectedCategoryFilter && this.selectedCategoryFilter !== 'all') {
				result = result.map(group => {
					return group.filter(t => {
						const classification = t.customerClassification || t.effectiveClassification;

						// Filter uncategorized
						if (this.selectedCategoryFilter === 'uncategorized') {
							return !classification || classification.mgClassification === 'Uncategorized';
						}

						// Filter by parent category (grandparent)
						if (this.selectedCategoryFilter.startsWith('parent:')) {
							const grandparent = parseInt(this.selectedCategoryFilter.split(':')[1]);
							return classification && classification.grandParentClassification === grandparent;
						}

						// Filter by specific category ID
						return classification && classification.id === this.selectedCategoryFilter;
					});
				}).filter(group => group.length > 0);
			}

			// Search filter
			if (this.searchQuery) {
				const q = this.searchQuery.trim().toLowerCase();
				result = result.map(group => {
					return group.filter(t =>
						t.merchantName?.toLowerCase().includes(q) ||
						(t.merchant?.name || '').toLowerCase().includes(q) ||
						(t.effectiveClassification?.mgClassification || '').toLowerCase().includes(q) ||
						(t.effectiveClassification?.mgSubClassification || '').toLowerCase().includes(q) ||
						t.amount.toString().includes(q) ||
						(t.customerTags || []).some(tag => tag.tag.toLowerCase().includes(q))
					);
				}).filter(group => group.length > 0);
			}

			// Tag filter
			if (this.selectedTags.length > 0) {
				result = result.map(group => {
					return group.filter(t => {
						const transactionTags = (t.customerTags || []).map(tag => tag.tag);
						return this.selectedTags.some(tag => transactionTags.includes(tag));
					});
				}).filter(group => group.length > 0);
			}

			// Date filter
			if (this.dateFrom || this.dateTo) {
				const fromDate = this.dateFrom ? new Date(this.dateFrom) : null;
				const toDate = this.dateTo ? new Date(this.dateTo) : null;
				if (toDate) toDate.setHours(23, 59, 59, 999);

				result = result.map(group => {
					return group.filter(t => {
						const txDate = new Date(t.timestamp);
						if (fromDate && txDate < fromDate) return false;
						if (toDate && txDate > toDate) return false;
						return true;
					});
				}).filter(group => group.length > 0);
			}

			return result;
		},
		transactionStats() {
			const allTxns = this.displayTransactions.flat();
			const count = allTxns.length;

			// Total spent (negative amounts = spending)
			const totalSpent = allTxns
				.filter(t => t.amount < 0)
				.reduce((sum, t) => sum + Math.abs(t.amount), 0);

			// Top merchant by spend
			const merchantSpend = {};
			allTxns.filter(t => t.amount < 0).forEach(t => {
				const name = t.merchantName || t.merchant?.name || 'Unknown';
				merchantSpend[name] = (merchantSpend[name] || 0) + Math.abs(t.amount);
			});

			let topMerchant = null;
			let topAmount = 0;
			Object.entries(merchantSpend).forEach(([name, amount]) => {
				if (amount > topAmount) {
					topMerchant = name;
					topAmount = amount;
				}
			});

			return { count, totalSpent, topMerchant, topMerchantAmount: topAmount };
		},
		uncategorizedTransactions() {
			if (!this.transactions) return [];
			const uncategorized = [];
			this.transactions.forEach(group => {
				group.forEach(t => {
					const classification = t.customerClassification || t.effectiveClassification;
					if (!classification || classification.mgClassification === 'Uncategorized') {
						uncategorized.push(t);
					}
				});
			});
			return uncategorized;
		},
		dateRangeLabel() {
			if (!this.dateFrom && !this.dateTo) return 'Date';
			if (this.dateFrom && this.dateTo) {
				const from = new Date(this.dateFrom).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
				const to = new Date(this.dateTo).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
				return `${from} - ${to}`;
			}
			if (this.dateFrom) {
				const from = new Date(this.dateFrom).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
				return `From ${from}`;
			}
			const to = new Date(this.dateTo).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
			return `To ${to}`;
		}
	},
	methods: {
		updatePref(key, value) {
			if (this.customer?.preferences) {
				this.customer.preferences[key] = value;
				this._savePreferences();
			}
		},
		selectColourMode(mode) {
			this.colourMode = mode;
			localStorage.setItem('mg_colour_mode', mode);
			this.$emit('colour-mode-changed', mode);
		},
		loadData() {
			this._getCustomer(() => {
				if (this.customer?.person?.firstName) {
					this.userName = this.customer.person.firstName;
				}
				this.getBankInfo();
				// Fetch available tags
				this._getCustomerTags(() => {
					this.availableTags = this.customerTags?.map(t => t.tag) || [];
				});
				// Fetch classifications for filter dropdown
				this._getTransactionClassifications((classifications) => {
					this.buildCategoryFilterOptions(classifications);
				});
				// Fetch recurring payments for sidebar
				this.fetchAndRegisterRecurringPayments();
			});
		},
		getBankInfo() {
			this._getBankingInfo(() => {
				this.currentAccounts = [];
				this.creditAccounts = [];
				this.savingAccounts = [];

				if (this.bankInfo?.accounts) {
					this.bankInfo.accounts.forEach(a => {
						if (a.accountType?.prettyName === 'Current') {
							this.currentAccounts.push(a);
						} else if (a.accountType?.prettyName === 'Credit') {
							this.creditAccounts.push(a);
						} else if (a.accountType?.prettyName === 'Savings') {
							this.savingAccounts.push(a);
						}
					});
				}

				this.transactions = this.bankInfo?.recentTransactions || [];
				this.bankInfoLoaded = true;
				this.$nextTick(() => this.updateScrollArrows());
			});
		},
		formatBalanceWithPence(amount) {
			const formatted = this.formatter.format(Math.abs(amount));
			const parts = formatted.split('.');
			if (parts.length === 2) {
				return `${amount < 0 ? '-' : ''}${parts[0]}.<span class="pence">${parts[1]}</span>`;
			}
			return formatted;
		},
		openTaggingModal(transaction) {
			this.selectedTransaction = transaction;
			this.bulkMode = false;
			this.showTransactionModal = true;
		},
		startCategorizing() {
			if (this.uncategorizedTransactions.length === 0) return;
			this.bulkMode = true;
			this.bulkModeIndex = 0;
			this.selectedTransaction = this.uncategorizedTransactions[0];
			this.showTransactionModal = true;
		},
		closeModal() {
			this.showTransactionModal = false;
			this.selectedTransaction = null;
			this.bulkMode = false;
			this.bulkModeIndex = 0;
		},
		onNextUncategorized() {
			// Move to next uncategorized transaction
			// Re-compute to get fresh list after tagging
			const remaining = this.uncategorizedTransactions;
			if (remaining.length === 0) {
				this.closeModal();
				return;
			}
			// Stay at index 0 since we just removed one from the list
			this.bulkModeIndex = 0;
			this.selectedTransaction = remaining[0];
		},
		onTransactionTagged(updatedTransaction) {
			// If "always apply" was used, refresh all transactions as the merchant rule affects multiple transactions
			if (updatedTransaction?.alwaysApplyTag) {
				this._clearBankingInfo();
				this.getBankInfo();
				return;
			}
			// Update the transaction in the local list
			if (this.transactions && updatedTransaction) {
				this.transactions.forEach(group => {
					const index = group.findIndex(t => t.id === updatedTransaction.id);
					if (index !== -1) {
						group.splice(index, 1, { ...group[index], ...updatedTransaction });
					}
				});
			}
		},
		handleTagsChange({ transaction, tags }) {
			const updatedTags = tags.map(newTag => {
				const existingTag = transaction.customerTags?.find(t => t.tag === newTag);
				if (existingTag) {
					return { tag: existingTag.tag, id: existingTag.id };
				}
				return { tag: newTag };
			});

			this._updateTransactionTags(transaction.bankAccountID, transaction.id, updatedTags, () => {
				this._clearCustomerTagsCache();
				// Update the transaction in the local list
				this.transactions.forEach(group => {
					const index = group.findIndex(t => t.id === transaction.id);
					if (index !== -1) {
						group[index].customerTags = updatedTags;
					}
				});
				// Refresh available tags
				this._getCustomerTags(() => {
					this.availableTags = this.customerTags?.map(t => t.tag) || [];
				});
			});
		},
		buildCategoryFilterOptions(classifications) {
			const parentGroups = [
				{ name: 'Income', grandparent: GRANDPARENT_CLASSIFICATIONS.INCOME, color: '#B4D8D7' },
				{ name: 'Future Plans', grandparent: GRANDPARENT_CLASSIFICATIONS.FUTURE_PLANNING, color: '#D5C9C8' },
				{ name: 'Everyday', grandparent: GRANDPARENT_CLASSIFICATIONS.EVERYDAY_SPEND, color: '#9EA2D0' },
				{ name: 'Committed', grandparent: GRANDPARENT_CLASSIFICATIONS.FIXED_COSTS, color: '#413655' }
			];

			const options = [];

			parentGroups.forEach(parent => {
				const categories = classifications.filter(
					c => c.mgClassification.grandParentClassification === parent.grandparent
				);

				if (categories.length > 0) {
					options.push({
						title: parent.name,
						value: `parent:${parent.grandparent}`,
						color: parent.color,
						children: categories.map(cat => ({
							title: cat.mgClassification.fullName,
							value: cat.mgClassification.id
						}))
					});
				}
			});

			this.categoryFilterOptions = options;
		},
		selectCategory(value, label) {
			this.selectedCategoryFilter = value;
			this.selectedCategoryLabel = label;
			this.categoryMenuOpen = false;
		},
		toggleParentExpand(parentValue) {
			this.expandedParents[parentValue] = !this.expandedParents[parentValue];
		},
		toggleTag(tag) {
			const index = this.selectedTags.indexOf(tag);
			if (index === -1) {
				this.selectedTags.push(tag);
			} else {
				this.selectedTags.splice(index, 1);
			}
		},
		clearDates() {
			this.dateFrom = null;
			this.dateTo = null;
		},
		onRandomiseFiguresChanged() {
			this.formatter.randomiseFigures = null;
			this.$forceUpdate();
		},
		refreshData() {
			this.bankInfoLoaded = false;
			this.loadData();
		},
		searchBackTransactions() {
			this._searchTransactions(this.searchQuery, (result) => {
				this.transactions = result;
			});
		},
		scrollTabs(direction) {
			const container = this.$refs.filterTabs;
			if (!container) return;
			const scrollAmount = 200;
			container.scrollBy({
				left: direction === 'left' ? -scrollAmount : scrollAmount,
				behavior: 'smooth'
			});
		},
		updateScrollArrows() {
			const container = this.$refs.filterTabs;
			if (!container) return;
			this.canScrollLeft = container.scrollLeft > 0;
			this.canScrollRight = container.scrollLeft < container.scrollWidth - container.clientWidth - 1;
		},
		downloadCsv() {
			const transactions = this.displayTransactions.flat();
			if (!transactions.length) return;

			const headers = ['Date', 'Description', 'Category', 'Tags', 'Amount'];
			const rows = transactions.map(t => {
				const date = new Date(t.timestamp).toLocaleDateString('en-GB');
				const description = (t.merchantName || t.merchant?.name || '').replace(/,/g, ' ');
				const category = t.effectiveClassification?.mgSubClassification || t.effectiveClassification?.mgClassification || 'Uncategorized';
				const tags = (t.customerTags || []).map(tag => '#' + tag.tag).join(' ');
				const amount = t.amount.toFixed(2);
				return [date, description, category, tags, amount].map(v => `"${v}"`).join(',');
			});

			const csv = [headers.join(','), ...rows].join('\n');
			const blob = new Blob([csv], { type: 'text/csv' });
			const url = URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = url;
			const accountName = this.selectedAccount ? (this.selectedAccount.bank?.name || 'Account') : 'All_Accounts';
			a.download = `transactions_${accountName}_${new Date().toISOString().split('T')[0]}.csv`;
			a.click();
			URL.revokeObjectURL(url);
		}
	},
	mounted() {
		const savedMode = localStorage.getItem('mg_colour_mode');
		if (savedMode) {
			this.colourMode = savedMode;
		} else {
			this.colourMode = 'light';
			localStorage.setItem('mg_colour_mode', 'light');
		}
		this.loadData();
		this.registerRecurringPaymentsSidebar();
		window.addEventListener('randomise-figures-changed', this.onRandomiseFiguresChanged);
		this.$nextTick(() => this.updateScrollArrows());
	},
	beforeUnmount() {
		window.removeEventListener('randomise-figures-changed', this.onRandomiseFiguresChanged);
	}
}
</script>

<style scoped>
.desktop-transactions-page {
	height: 100%;
	overflow-y: auto;
	background: var(--background-color-main);
}

.transactions-content {
	padding: 24px;
	max-width: 1200px;
	margin: 0 auto;
}

/* Page Header */
.page-header {
	margin-bottom: 24px;
}

.page-title {
	font-size: 28px;
	margin: 0;
	color: var(--text-color);
}

/* Loading State */
.loading-state {
	padding: 20px 0;
}

/* Account Summary Card */
.account-summary-card {
	background: var(--light-bg-primary);
	border-radius: 12px;
	padding: 20px 24px;
	margin-bottom: 24px;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
	border-left: 4px solid var(--brand-purple);
}

.card-header {
	display: flex;
	align-items: center;
	gap: 16px;
	padding-bottom: 20px;
	border-bottom: 1px solid var(--light-border);
}

.bank-logo-frame {
	width: 56px;
	height: 40px;
	border-radius: 6px;
	display: flex;
	align-items: center;
	justify-content: center;
	background: linear-gradient(135deg, var(--brand-purple) 0%, var(--brand-purple-light) 100%);
	flex-shrink: 0;
}

.bank-logo {
	width: 32px;
	height: 32px;
	object-fit: contain;
}

.bank-logo-placeholder {
	color: white;
	font-size: 18px;
	font-weight: bold;
}

.bank-details {
	flex: 1;
}

.bank-name {
	font-size: 16px;
	font-weight: bold;
	color: var(--light-text-primary);
	margin-bottom: 2px;
}

.account-number {
	font-size: 13px;
	color: var(--light-text-secondary);
}

.card-balances {
	padding-top: 16px;
	display: flex;
	gap: 32px;
	flex-wrap: wrap;
	justify-content: space-between;
}

.stat-column {
	display: flex;
	flex-direction: column;
	gap: 4px;
}

.stat-column-small {
	min-width: 80px;
}

.stat-column-merchant {
	min-width: 250px;
}

.stat-label {
	font-size: 12px;
	color: var(--light-text-secondary);
}

.stat-amount {
	font-size: 20px;
	color: var(--light-text-primary);
}

.stat-amount.stat-merchant {
	font-size: 16px;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	max-width: 250px;
}

.stat-subtext {
	font-size: 13px;
	color: var(--light-text-secondary);
}

.stat-amount :deep(.pence) {
	font-size: 12px;
	vertical-align: super;
}

/* Filter Tabs Wrapper */
.filter-tabs-wrapper {
	display: flex;
	align-items: center;
	gap: 8px;
	margin-bottom: 16px;
	position: sticky;
	top: 0;
	z-index: 10;
	background: var(--background-color-main);
	padding-top: 16px;
	margin-top: -16px;
}

.scroll-arrow {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 32px;
	height: 32px;
	border: 1px solid var(--light-border);
	border-radius: 50%;
	background: var(--light-bg-primary);
	color: var(--brand-purple);
	cursor: pointer;
	flex-shrink: 0;
	transition: all 0.2s;
}

.scroll-arrow:hover {
	background: var(--brand-purple);
	border-color: var(--brand-purple);
	color: white;
}

/* Filter Tabs */
.filter-tabs {
	display: flex;
	gap: 8px;
	overflow-x: auto;
	padding-bottom: 8px;
	flex: 1;
	scrollbar-width: none; /* Firefox */
	-ms-overflow-style: none; /* IE/Edge */
}

.filter-tabs::-webkit-scrollbar {
	display: none; /* Chrome, Safari, Opera */
}

.filter-tab {
	padding: 10px 20px;
	border: none;
	border-radius: 8px;
	background: var(--light-bg-primary);
	color: var(--brand-purple);
	cursor: pointer;
	white-space: nowrap;
	font-size: 13px;
	transition: all 0.2s;
	display: flex;
	align-items: center;
	gap: 8px;
}

.tab-logo {
	width: 20px;
	height: 20px;
	border-radius: 4px;
	object-fit: contain;
}

.tab-content {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 2px;
}

.tab-label {
	font-size: 13px;
}

.tab-balance {
	font-size: 11px;
	opacity: 0.8;
}

.filter-tab.active {
	background: var(--brand-purple);
	color: white;
}

.filter-tab:hover:not(.active) {
	background: var(--light-border);
}

/* Search Section */
.search-section {
	margin-bottom: 24px;
	display: flex;
	align-items: center;
	gap: 16px;
	position: sticky;
	top: 78px;
	z-index: 9;
	background: var(--background-color-main);
	padding: 12px 0;
}

.search-section .v-text-field {
	max-width: 300px;
}

/* Category Filter Dropdown */
.category-filter-btn {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 8px 12px;
	border: 1px solid var(--light-border);
	border-radius: 4px;
	background: var(--light-bg-primary);
	color: var(--light-text-primary);
	cursor: pointer;
	font-size: 14px;
	min-width: 180px;
	justify-content: space-between;
}

.category-filter-btn:hover {
	background: var(--light-bg-hover);
}

.category-menu {
	min-width: 220px;
	max-height: 400px;
	overflow-y: auto;
	padding: 8px 0 !important;
	border-radius: 8px !important;
}

.category-menu-item {
	padding: 10px 16px;
	cursor: pointer;
	font-size: 14px;
	color: var(--light-text-primary);
}

.category-menu-item:hover {
	background: var(--light-bg-hover);
}

.category-menu-item.selected {
	background: var(--brand-purple-highlight);
	color: var(--brand-purple);
}

.category-parent-group {
	margin: 4px 0;
}

.category-parent-item {
	display: flex;
	align-items: center;
	padding: 0 16px 0 0;
	font-size: 14px;
	color: var(--light-text-primary);
}

.category-parent-item.selected {
	background: var(--brand-purple-highlight);
}

.category-parent-item.selected .parent-name {
	color: var(--brand-purple);
}

.parent-selectable {
	display: flex;
	align-items: center;
	gap: 10px;
	flex: 1;
	padding: 10px 16px;
	cursor: pointer;
}

.parent-selectable:hover {
	background: var(--light-bg-hover);
}

.parent-color-dot {
	width: 12px;
	height: 12px;
	border-radius: 50%;
	flex-shrink: 0;
}

.parent-name {
	font-weight: bold;
}

.expand-icon {
	color: var(--light-text-secondary);
	cursor: pointer;
	padding: 10px 4px;
}

.expand-icon:hover {
	color: var(--light-text-primary);
}

.category-children {
	background: var(--light-bg-secondary);
}

.category-child-item {
	padding: 8px 16px 8px 38px;
	cursor: pointer;
	font-size: 13px;
	color: var(--light-text-secondary);
}

.category-child-item:hover {
	background: var(--light-bg-hover);
	color: var(--light-text-primary);
}

.category-child-item.selected {
	background: var(--brand-purple-highlight);
	color: var(--brand-purple);
}

/* Tag Filter */
.tag-filter-btn {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 8px 12px;
	border: 1px solid var(--light-border);
	border-radius: 4px;
	background: var(--light-bg-primary);
	color: var(--light-text-primary);
	cursor: pointer;
	font-size: 14px;
	transition: all 0.2s ease;
}

.tag-filter-btn:hover {
	background: var(--brand-purple-highlight);
	border-color: var(--brand-purple);
	color: var(--brand-purple);
}

/* Date Filter */
.date-filter-btn {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 8px 12px;
	border: 1px solid var(--light-border);
	border-radius: 4px;
	background: var(--light-bg-primary);
	color: var(--light-text-primary);
	cursor: pointer;
	font-size: 14px;
	transition: all 0.2s ease;
}

.date-filter-btn:hover {
	background: var(--brand-purple-highlight);
	border-color: var(--brand-purple);
	color: var(--brand-purple);
}

.date-menu {
	padding: 16px !important;
	border-radius: 8px !important;
	min-width: 240px;
}

.date-menu-content {
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.date-field {
	display: flex;
	flex-direction: column;
	gap: 4px;
}

.date-field label {
	font-size: 12px;
	color: var(--light-text-secondary);
}

.date-input {
	padding: 8px 12px;
	border: 1px solid var(--light-border);
	border-radius: 4px;
	font-size: 14px;
	color: var(--light-text-primary);
	background: var(--light-bg-primary);
}

.date-input:focus {
	outline: none;
	border-color: var(--brand-purple);
}

.date-menu-actions {
	display: flex;
	justify-content: space-between;
	padding-top: 8px;
}

.clear-dates-btn {
	padding: 6px 12px;
	border: none;
	background: none;
	color: var(--light-text-secondary);
	cursor: pointer;
	font-size: 13px;
}

.clear-dates-btn:hover {
	color: var(--brand-purple);
}

.apply-dates-btn {
	padding: 6px 16px;
	border: none;
	border-radius: 4px;
	background: var(--brand-purple);
	color: white;
	cursor: pointer;
	font-size: 13px;
}

.apply-dates-btn:hover {
	background: var(--brand-purple-light);
}

/* Refresh Button */
.refresh-btn {
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 8px;
	border: 1px solid var(--light-border);
	border-radius: 4px;
	background: var(--light-bg-primary);
	color: var(--light-text-secondary);
	cursor: pointer;
	transition: all 0.2s ease;
	margin-left: auto;
}

.refresh-btn:hover {
	background: var(--light-bg-hover);
	color: var(--brand-purple);
	border-color: var(--brand-purple);
}

/* Download CSV Button */
.download-csv-btn {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 8px 12px;
	border: 1px solid var(--brand-purple);
	border-radius: 4px;
	background: var(--brand-purple);
	color: white;
	cursor: pointer;
	font-size: 14px;
	transition: all 0.2s ease;
}

.download-csv-btn:hover {
	background: var(--brand-purple-light);
	border-color: var(--brand-purple-light);
}

.tag-menu {
	min-width: 200px;
	max-height: 300px;
	overflow-y: auto;
	padding: 8px 0 !important;
	border-radius: 8px !important;
}

.tag-menu-item {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 6px 16px;
	cursor: pointer;
	font-size: 14px;
	color: var(--light-text-primary);
}

.tag-menu-item:hover {
	background: var(--light-bg-hover);
}

.tag-checkbox {
	flex-shrink: 0;
}

.tag-label {
	flex: 1;
}

.no-tags {
	padding: 16px;
	text-align: center;
	color: var(--light-text-secondary);
	font-size: 14px;
}

.clear-tags {
	padding: 10px 16px;
	cursor: pointer;
	font-size: 13px;
	color: var(--brand-purple);
	text-align: center;
}

.clear-tags:hover {
	background: var(--light-bg-hover);
}

/* Uncategorized Banner */
.uncategorized-banner {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 14px 20px;
	background: var(--suggestion-bg, #f0edf5);
	border: 1px solid var(--suggestion-border, #d4cce0);
	border-radius: 8px;
	margin-bottom: 16px;
}

.banner-content {
	display: flex;
	align-items: center;
	gap: 10px;
	color: var(--brand-purple);
}

.banner-text {
	font-size: 14px;
	color: var(--light-text-primary);
}

.start-categorizing-btn {
	background: var(--brand-purple);
	border: none;
	padding: 8px 16px;
	border-radius: 6px;
	font-size: 13px;
	cursor: pointer;
	color: white;
	transition: background 0.15s;
}

.start-categorizing-btn:hover {
	background: var(--brand-purple-light);
}

/* Search Further Back */
.search-further-back {
	display: flex;
	justify-content: center;
	padding: 24px 0;
}

.search-further-btn {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 10px 20px;
	border: 1px solid var(--light-border);
	border-radius: 8px;
	background: var(--light-bg-primary);
	color: var(--brand-purple);
	cursor: pointer;
	font-size: 14px;
	transition: all 0.2s ease;
}

.search-further-btn:hover {
	background: var(--brand-purple);
	border-color: var(--brand-purple);
	color: white;
}

</style>

<!-- Dark Mode Styles -->
<style>
.desktop-transactions-page.dark {
	background: var(--dark-bg-primary);
}

.desktop-transactions-page.dark .page-title {
	color: var(--dark-text-primary);
}

.desktop-transactions-page.dark .account-summary-card {
	background: var(--dark-bg-secondary);
}

.desktop-transactions-page.dark .card-header {
	border-color: var(--dark-border);
}

.desktop-transactions-page.dark .bank-name {
	color: var(--dark-text-primary);
}

.desktop-transactions-page.dark .account-number,
.desktop-transactions-page.dark .stat-label,
.desktop-transactions-page.dark .stat-subtext {
	color: var(--dark-text-secondary);
}

.desktop-transactions-page.dark .stat-amount {
	color: var(--dark-text-primary);
}

.desktop-transactions-page.dark .filter-tabs-wrapper {
	background: var(--dark-bg-primary);
}

.desktop-transactions-page.dark .scroll-arrow {
	background: var(--dark-bg-secondary);
	border-color: var(--dark-border);
	color: var(--brand-purple-light);
}

.desktop-transactions-page.dark .scroll-arrow:hover {
	background: var(--brand-purple);
	border-color: var(--brand-purple);
	color: white;
}

.desktop-transactions-page.dark .filter-tab {
	background: var(--dark-bg-secondary);
	color: var(--dark-text-primary);
}

.desktop-transactions-page.dark .filter-tab.active {
	background: var(--brand-purple-light);
	color: white;
}

.desktop-transactions-page.dark .filter-tab:hover:not(.active) {
	background: var(--dark-bg-hover);
}

.desktop-transactions-page.dark .search-section {
	background: var(--dark-bg-primary);
}

.desktop-transactions-page.dark .search-section .v-field {
	background: var(--dark-bg-secondary) !important;
}

.desktop-transactions-page.dark .search-section input {
	color: var(--dark-text-primary) !important;
}

/* Category Menu Dark Mode */
.desktop-transactions-page.dark .category-filter-btn {
	background: var(--dark-bg-secondary);
	border-color: var(--dark-border);
	color: var(--dark-text-primary);
}

.desktop-transactions-page.dark .category-filter-btn:hover {
	background: var(--dark-bg-hover);
}

.category-menu.dark {
	background: #1e1e1e !important;
}

.category-menu.dark .category-menu-item {
	color: var(--dark-text-primary);
}

.category-menu.dark .category-menu-item:hover {
	background: var(--dark-bg-hover);
}

.category-menu.dark .category-menu-item.selected {
	background: var(--brand-purple);
	color: white;
}

.category-menu.dark .category-parent-item {
	color: var(--dark-text-primary);
}

.category-menu.dark .category-parent-item.selected {
	background: var(--brand-purple);
}

.category-menu.dark .category-parent-item.selected .parent-name {
	color: white;
}

.category-menu.dark .parent-selectable:hover {
	background: var(--dark-bg-hover);
}

.category-menu.dark .expand-icon {
	color: var(--dark-text-secondary);
}

.category-menu.dark .category-children {
	background: var(--dark-bg-primary);
}

.category-menu.dark .category-child-item {
	color: var(--dark-text-secondary);
}

.category-menu.dark .category-child-item:hover {
	background: var(--dark-bg-hover);
	color: var(--dark-text-primary);
}

.category-menu.dark .category-child-item.selected {
	background: var(--brand-purple);
	color: white;
}

/* Tag Filter Dark Mode */
.desktop-transactions-page.dark .tag-filter-btn {
	background: var(--dark-bg-secondary);
	border-color: var(--dark-border);
	color: var(--dark-text-primary);
}

.desktop-transactions-page.dark .tag-filter-btn:hover {
	background: var(--brand-purple);
	border-color: var(--brand-purple);
	color: white;
}

/* Date Filter Dark Mode */
.desktop-transactions-page.dark .date-filter-btn {
	background: var(--dark-bg-secondary);
	border-color: var(--dark-border);
	color: var(--dark-text-primary);
}

.desktop-transactions-page.dark .date-filter-btn:hover {
	background: var(--brand-purple);
	border-color: var(--brand-purple);
	color: white;
}

.date-menu.dark {
	background: #1e1e1e !important;
}

.date-menu.dark .date-field label {
	color: var(--dark-text-secondary);
}

.date-menu.dark .date-input {
	background: var(--dark-bg-secondary);
	border-color: var(--dark-border);
	color: var(--dark-text-primary);
}

.date-menu.dark .clear-dates-btn {
	color: var(--dark-text-secondary);
}

.date-menu.dark .clear-dates-btn:hover {
	color: var(--brand-purple-light);
}

.desktop-transactions-page.dark .refresh-btn {
	background: var(--dark-bg-secondary);
	border-color: var(--dark-border);
	color: var(--dark-text-secondary);
}

.desktop-transactions-page.dark .refresh-btn:hover {
	background: var(--dark-bg-hover);
	color: var(--brand-purple-light);
	border-color: var(--brand-purple-light);
}

.desktop-transactions-page.dark .download-csv-btn {
	background: var(--brand-purple-light);
	border-color: var(--brand-purple-light);
}

.desktop-transactions-page.dark .download-csv-btn:hover {
	background: var(--brand-purple);
	border-color: var(--brand-purple);
}

.tag-menu.dark {
	background: #1e1e1e !important;
}

.tag-menu.dark .tag-menu-item {
	color: var(--dark-text-primary);
}

.tag-menu.dark .tag-menu-item:hover {
	background: var(--dark-bg-hover);
}

.tag-menu.dark .no-tags {
	color: var(--dark-text-secondary);
}

.tag-menu.dark .clear-tags:hover {
	background: var(--dark-bg-hover);
}

/* Uncategorized Banner Dark Mode */
.desktop-transactions-page.dark .uncategorized-banner {
	background: var(--suggestion-bg-dark, rgba(65, 54, 85, 0.3));
	border-color: var(--suggestion-border-dark, #5a4a70);
}

.desktop-transactions-page.dark .banner-text {
	color: var(--dark-text-primary);
}

/* Search Further Back Dark Mode */
.desktop-transactions-page.dark .search-further-btn {
	background: var(--dark-bg-secondary);
	border-color: var(--dark-border);
	color: var(--brand-purple-light);
}

.desktop-transactions-page.dark .search-further-btn:hover {
	background: var(--brand-purple);
	border-color: var(--brand-purple);
	color: white;
}
</style>
